// 合同相关API请求

import axios from 'axios'
import { getToken } from '@/utils/auth'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '',
  timeout: 15000 // 请求超时时间
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 请求头添加token
    const token = getToken()
    if (token) {
      config.headers['Authorization'] = token
    }
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    // 根据后端接口规范处理响应
    if (res.code && res.code !== 200) {
      console.error('接口错误:', res.message || '未知错误')
      return Promise.reject(new Error(res.message || '未知错误'))
    } else {
      return res
    }
  },
  error => {
    console.error('响应错误:', error)
    return Promise.reject(error)
  }
)

/**
 * 获取合同列表
 * @param {Object} params - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function getContractList (params) {
  return service({
    url: '/contract/list',
    method: 'get',
    params
  })
}

/**
 * 获取合同详情
 * @param {string|number} id - 合同ID
 * @returns {Promise} 请求Promise对象
 */
export function getContractDetail (id) {
  return service({
    url: `/contract/detail/${id}`,
    method: 'get'
  })
}

/**
 * 创建合同
 * @param {Object} data - 合同数据
 * @returns {Promise} 请求Promise对象
 */
export function createContract (data) {
  return service({
    url: '/contract/create',
    method: 'post',
    data
  })
}

/**
 * 更新合同
 * @param {Object} data - 合同数据
 * @returns {Promise} 请求Promise对象
 */
export function updateContract (data) {
  return service({
    url: '/contract/update',
    method: 'put',
    data
  })
}

/**
 * 删除合同
 * @param {string|number} id - 合同ID
 * @returns {Promise} 请求Promise对象
 */
export function deleteContract (id) {
  return service({
    url: `/contract/delete/${id}`,
    method: 'delete'
  })
}

/**
 * 保存标准
 * @param {Object} data - 标准数据
 * @returns {Promise} 请求Promise对象
 */
export function saveStandard (data) {
  return service({
    url: '/api/standard/saveStandard',
    method: 'post',
    data
  })
}

/**
 * 查询标准
 * @param {Object} data - 标准数据
 * @returns {Promise} 请求Promise对象
 */
export function queryStandard (data) {
  return service({
    url: '/api/standard/queryStandard',
    method: 'post',
    data
  })
}

/**
 * 更新标准
 * @param {Object} data - 标准数据
 * @returns {Promise} 请求Promise对象
 */
export function updateStandard (data) {
  return service({
    url: '/api/standard/updateStandard',
    method: 'post',
    data
  })
}

/**
 * 删除标准
 * @param {string|number} id - 标准ID
 * @returns {Promise} 请求Promise对象
 */
export function deleteStandard (id) {
  return service({
    url: '/api/standard/deleteStandard',
    method: 'post',
    data: { standardPid: id } // 后端需要的是 standardPid 字段
  })
}

/**
 * 导出标准
 * @param {Object} params - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function exportStandard (params) {
  return service({
    url: '/api/standard/exportStandard',
    method: 'get',
    params: params,
    responseType: 'blob' // 重要：设置响应类型为 blob 以处理文件下载
  })
}

/**
 * 查询模板
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTemplate (data) {
  return service({
    url: '/api/template/queryTemplate',
    method: 'post',
    data: data
  })
}

/**
 * 保存模板
 * @param {Object} data - 模板数据
 * @returns {Promise} 请求Promise对象
 */
export function saveTemplate (data) {
  return service({
    url: '/api/template/saveTemplate',
    method: 'post',
    data: data
  })
}

/**
 * 查询所有检测报告
 * @returns {Promise} 请求Promise对象
 */
export function queryAllConfigReport () {
  return service({
    url: '/api/template/queryAllConfigReport',
    method: 'get'
  })
}

/**
 * 删除模板
 * @param {string|number} id - 模板ID
 * @returns {Promise} 请求Promise对象
 */
export function deleteTemplate (id) {
  return service({
    url: '/api/template/deleteTemplate',
    method: 'post',
    data: { templatePid: id }
  })
}

/**
 * 导出模板
 * @param {Object} params - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function exportTemplate (params) {
  return service({
    url: '/api/template/exportTemplate',
    method: 'get',
    params: params,
    responseType: 'blob' // 重要：设置响应类型为 blob 以处理文件下载
  })
}

/**
 * 更新模板
 * @param {Object} data - 模板数据
 * @returns {Promise} 请求Promise对象
 */
export function updateTemplate (data) {
  return service({
    url: '/api/template/updateTemplate',
    method: 'post',
    data: data
  })
}

//模板下载
/**
 * 模板下载
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function downloadTemplate (data) {
  return service({
    url: '/api/template/downloadTemplate',
    method: 'post',
    data: data,
    responseType: 'blob' // 重要：设置响应类型为 blob 以处理文件下载
  })
}

/**
 * 查询所有检测报告
 * @returns {Promise} 请求Promise对象
 */
export function templateQueryAllConfigReport () {
  return service({
    url: '/api/reportType/queryReportTypeTree',
    method: 'get'
  })
}

/**
 * 查询检测报告详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryReportTypeDetail (data) {
  return service({
    url: '/api/reportType/queryReportTypeDetail',
    method: 'post',
    data: data
  })
}

/**
 * 查询检测报告详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryConfigReportDetail (data) {
  return service({
    url: '/api/configReport/queryConfigReportDetail',
    method: 'post',
    data: data
  })
}

/**
 * 查询检测项目详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryConfigTestItemDetail (data) {
  return service({
    url: '/api/configTestItems/queryConfigTestItemsDetail',
    method: 'post',
    data: data
  })
}

//查询检测项目子表详情
/**
 * 查询检测项目子表详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryConfigTestItemsChildDetail (data) {
  return service({
    url: '/api/configTestItemsChild/queryConfigTestItemsChildDetail',
    method: 'post',
    data: data
  })
}

//查询检测项目子表内容详情
/**
 * 查询检测项目子表内容详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryConfigTestItemsContentDetail (data) {
  return service({
    url: '/api/configTestItemsContent/queryContentListByChildPid',
    method: 'post',
    data: data
  })
}

//新增报告类别
/**
 * 新增报告类别
 * @param {Object} data - 报告类别数据
 * @returns {Promise} 请求Promise对象
 */
export function saveReportType (data) {
  return service({
    url: '/api/reportType/saveReportType',
    method: 'post',
    data: data
  })
}

/**
 * 新增检测报告
 * @param {Object} data - 检测报告数据
 * @returns {Promise} 请求Promise对象
 */
export function saveConfigReport (data) {
  return service({
    url: '/api/configReport/saveConfigReport',
    method: 'post',
    data: data
  })
}

//保存检测项目详情
/**
 * 新增检测项目
 * @param {Object} data - 检测项目数据
 * @returns {Promise} 请求Promise对象
 */
export function saveConfigTestItems (data) {
  return service({
    url: '/api/configTestItems/saveConfigTestItems',
    method: 'post',
    data: data
  })
}

//保存检测项目子表详情
/**
 * 保存检测项目子表详情
 * @param {Object} data - 检测项目子表数据
 * @returns {Promise} 请求Promise对象
 */
export function saveConfigTestItemsChild (data) {
  return service({
    url: '/api/configTestItemsChild/saveConfigTestItemsChild',
    method: 'post',
    data: data
  })
}

//批量保存检测项目子表内容详情
/**
 * 保存检测项目子表内容详情
 * @param {Object} data - 检测项目子表内容数据
 * @returns {Promise} 请求Promise对象
 */
export function saveConfigTestItemsContent (data) {
  return service({
    url: '/api/configTestItemsContent/saveConfigTestItemsContentBatch',
    method: 'post',
    data: data
  })
}

//修改报告类别
/**
 * 修改报告类别
 * @param {Object} data - 报告类别数据
 * @returns {Promise} 请求Promise对象
 */
export function updateReportType (data) {
  return service({
    url: '/api/reportType/updateReportType',
    method: 'post',
    data: data
  })
}

//更新检测报告详情
/**
 * 更新检测报告详情
 * @param {Object} data - 检测报告数据
 * @returns {Promise} 请求Promise对象
 */
export function updateConfigReport (data) {
  return service({
    url: '/api/configReport/updateConfigReport',
    method: 'post',
    data: data
  })
}

//更新检测项目详情
/**
 * 更新检测项目详情
 * @param {Object} data - 检测项目数据
 * @returns {Promise} 请求Promise对象
 */
export function updateConfigTestItems (data) {
  return service({
    url: '/api/configTestItems/updateConfigTestItems',
    method: 'post',
    data: data
  })
}

//更新检测项目子表详情
/**
 * 更新检测项目子表详情
 * @param {Object} data - 检测项目子表数据
 * @returns {Promise} 请求Promise对象
 */
export function updateConfigTestItemsChild (data) {
  return service({
    url: '/api/configTestItemsChild/updateConfigTestItemsChild',
    method: 'post',
    data: data
  })
}

//批量更新检测项目子表内容详情
/**
 * 批量更新检测项目子表内容详情
 * @param {Object} data - 检测项目子表内容数据
 * @returns {Promise} 请求Promise对象
 */
export function updateConfigTestItemsContent (data) {
  return service({
    url: '/api/configTestItemsContent/updateConfigTestItemsContentBatch',
    method: 'post',
    data: data
  })
}

//删除报告类别
/**
 * 删除报告类别
 * @param {Object} data - 报告类别数据
 * @returns {Promise} 请求Promise对象
 */
export function deleteReportType (data) {
  return service({
    url: '/api/reportType/deleteReportType',
    method: 'post',
    data: data
  })
}

//删除检测报告(逻辑删除 修改enablestate)
/**
 * 删除检测报告(逻辑删除 修改enablestate)
 * @param {Object} data - 检测报告数据
 * @returns {Promise} 请求Promise对象
 */
export function deleteConfigReport (data) {
  return service({
    url: '/api/configReport/deleteConfigReport',
    method: 'post',
    data: data
  })
}

//删除检测项目详情
/**
 * 删除检测项目详情
 * @param {Object} data - 检测项目数据
 * @returns {Promise} 请求Promise对象
 */
export function deleteConfigTestItems (data) {
  return service({
    url: '/api/configTestItems/deleteConfigTestItems',
    method: 'post',
    data: data
  })
}

//删除检测项目子表详情
/**
 * 删除检测项目子表详情
 * @param {Object} data - 检测项目子表数据
 * @returns {Promise} 请求Promise对象
 */
export function deleteConfigTestItemsChild (data) {
  return service({
    url: '/api/configTestItemsChild/deleteConfigTestItemsChild',
    method: 'post',
    data: data
  })
}

//删除检测项目子表详情
/**
 * 删除检测项目子表详情
 * @param {Object} data - 检测项目子表数据
 * @returns {Promise} 请求Promise对象
 */
export function deleteContentByChildPid (data) {
  return service({
    url: '/api/configTestItemsContent/deleteContentByChildPid',
    method: 'post',
    data: data
  })
}

//-----------设备管理-----------//
//查询设备列表
/**
 * 查询设备列表
 * @param {Object} data - 设备数据
 * @returns {Promise} 请求Promise对象
 */
export function queryEqptList (data) {
  return service({
    url: '/api/equipment/base/list',
    method: 'post',
    data: data
  })
}

//新增设备
/**
 * 新增设备
 * @param {Object} data - 设备数据
 * @returns {Promise} 请求Promise对象
 */
export function saveEqpt (data) {
  return service({
    url: '/api/equipment/base/add',
    method: 'post',
    data: data
  })
}

//查询设备详情
/**
 * 查询设备详情
 * @param {Object} data - 设备数据
 * @returns {Promise} 请求Promise对象
 */
export function queryEqptDetail (data) {
  return service({
    url: '/api/equipment/base/' + data,
    method: 'get'
  })
}

//修改设备
/**
 * 修改设备
 * @param {Object} data - 设备数据
 * @returns {Promise} 请求Promise对象
 */
export function updateEqpt (data) {
  return service({
    url: '/api/equipment/base/update',
    method: 'put',
    data: data
  })
}

//导出设备excel
/**
 * 导出设备excel
 * @param {Object} data - 设备数据
 * @returns {Promise} 请求Promise对象
 */
export function exportEqptExcel (data) {
  // 注意：该接口返回的是文件流（blob），同时需要从响应头 Content-Disposition 里取文件名。
  // 因此这里不走 service 的响应拦截（它只返回 response.data，会丢失 headers），直接用 axios 请求。
  const token = getToken()
  return axios({
    baseURL: process.env.VUE_APP_BASE_API || '',
    url: '/api/equipment/export',
    method: 'post',
    data,
    responseType: 'blob',
    headers: token ? { Authorization: token } : undefined
  })
}

//下载设备导入模板
/**
 * 下载设备导入模板
 * @returns {Promise} 请求Promise对象
 */
export function downloadEqptTemplate () {
  const token = getToken()
  return axios({
    baseURL: process.env.VUE_APP_BASE_API || '',
    url: '/api/equipment/downloadTemplate',
    method: 'get',
    responseType: 'blob',
    headers: token ? { Authorization: token } : undefined
  })
}

//批量导入设备信息
/**
 * 批量导入设备信息
 * @param {FormData} formData - 包含文件的FormData对象
 * @returns {Promise} 请求Promise对象
 */
export function importEqptData (formData) {
  return service({
    url: '/api/equipment/importData',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

//-----------设备检定信息-----------//

//分页查询设备检定信息
/**
 * 分页查询设备检定信息
 * @param {Object} data - 设备检定信息数据
 * @returns {Promise} 请求Promise对象
 */
export function queryEqptVerificationList (data) {
  return service({
    url: '/api/equipment/calibration/list',
    method: 'post',
    data: data
  })
}

//新增设备检定信息
/**
 * 新增设备检定信息
 * @param {Object} data - 设备检定信息数据
 * @returns {Promise} 请求Promise对象
 */
export function saveEqptVerification (data) {
  return service({
    url: '/api/equipment/calibration/add',
    method: 'post',
    data: data
  })
}

//查询设备检定信息
/**
 * 查询设备检定信息
 * @param {Object} data - 设备检定信息数据
 * @returns {Promise} 请求Promise对象
 */
export function queryEqptVerification (data) {
  return service({
    url: '/api/equipment/calibration/' + data,
    method: 'get'
  })
}

//更新设备检定信息
/**
 * 更新设备检定信息
 * @param {Object} data - 设备检定信息数据
 * @returns {Promise} 请求Promise对象
 */
export function updateEqptVerification (data) {
  return service({
    url: '/api/equipment/calibration/update',
    method: 'put',
    data: data
  })
}

//导出设备excel
/**
 * 导出设备excel
 * @param {Object} data - 设备数据
 * @returns {Promise} 请求Promise对象
 */
export function exportEqptVerificationExcel (data) {
  return service({
    url: '/api/equipment/calibration/export',
    method: 'post',
    data: data
  })
}

//删除设备检定信息

/**
 * 删除设备检定信息
 * @param {Object} data - 设备检定信息数据
 * @returns {Promise} 请求Promise对象
 */
export function deleteEqptVerification (data) {
  return service({
    url: '/api/equipment/calibration/' + data,
    method: 'delete'
  })
}

//查询单个设备检定信息
/**
 * 查询单个设备检定信息
 * @param {Object} data - 设备检定信息数据
 * @returns {Promise} 请求Promise对象
 */
export function queryEqptVerificationByEqptNum (data) {
  return service({
    url: '/api/equipment/base/cals',
    method: 'post',
    data: data
  })
}

//模板查询
/**
 * 模板查询
 * @param {Object} data - 模板数据
 * @returns {Promise} 请求Promise对象
 */
export function queryAllTemplatePidAndName (data) {
  return service({
    url: '/api/template/queryAllTemplatePidAndName',
    method: 'post',
    data: data
  })
}

//标准查询
/**
 * 标准查询
 * @param {Object} data - 标准数据
 * @returns {Promise} 请求Promise对象
 */
export function queryAllStandardPidCodeName (data) {
  return service({
    url: '/api/standard/queryAllStandardPidCodeName',
    method: 'post',
    data: data
  })
}

//设备查询
/**
 * 设备查询
 * @param {Object} data - 设备数据
 * @returns {Promise} 请求Promise对象
 */
export function queryEqptGuidAndName (data) {
  return service({
    url: '/api/equipment/base/guidAndName',
    method: 'post',
    data: data
  })
}

//根据员工id查询签字权限
/**
 * 根据员工id查询签字权限
 * @param {Object} data - 员工数据
 * @returns {Promise} 请求Promise对象
 */
export function queryPermissionsByEmployee (data) {
  return service({
    url: '/api/employeeSignPermission/queryPermissionsByEmployee',
    method: 'get',
    params: data
  })
}

//批量保存和修改员工签字权限
/**
 * 批量保存和修改员工签字权限
 * @param {Object} data - 员工签字权限数据
 * @returns {Promise} 请求Promise对象
 */
export function batchSaveOrUpdateEmployeeSignPermission (data) {
  return service({
    url: '/api/employeeSignPermission/batchSaveOrUpdateEmployeeSignPermission',
    method: 'post',
    data: data
  })
}

//查询机构列表
/**
 * 查询机构列表
 * @param {Object} data - 机构数据
 * @returns {Promise} 请求Promise对象
 */
export function querySysDepartments (data) {
  return service({
    url: '/api/configReport/querySysDepartments',
    method: 'post',
    data: data
  })
}

/**
 * 查询可用监管设备名称
 * @returns {Promise} 请求Promise对象
 */
export function queryAvailableJgEqpNames () {
  return service({
    url: '/api/configReport/queryAvailableJgEqpNames',
    method: 'post'
  })
}

//---------------------客户检测信息管理----------------------------

//列表信息
/**
 * 列表信息
 * @param {Object} data - 客户检测信息数据
 * @returns {Promise} 请求Promise对象
 */
export function queryCustInfoList (data) {
  return service({
    url: '/api/custInfo/list',
    method: 'post',
    data: data
  })
}

//导入客户检测信息
/**
 * 导入客户检测信息
 * @param {Object} data - 客户检测信息数据
 * @returns {Promise} 请求Promise对象
 */
export function importCustInfo (data) {
  return service({
    url: '/api/custInfo/import',
    method: 'post',
    data: data
  })
}

//导出客户检测信息
/**
 * 导出客户检测信息
 * @param {Object} data - 客户检测信息数据
 * @returns {Promise} 请求Promise对象
 */
export function exportCustInfo (data) {
  return service({
    url: '/api/custInfo/export',
    method: 'post',
    data: data,
    responseType: 'blob' // 重要：设置响应类型为 blob 以处理文件下载
  })
}

//更改状态
/**
 * 更改状态
 * @param {String} enableState - 启用状态（0-已确认，1-未确认）
 * @param {String} custPid - 客户检测信息ID
 * @returns {Promise} 请求Promise对象
 */
export function updateCustInfoState (enableState, custPid) {
  return service({
    url: `/api/custInfo/${custPid}?enableState=${enableState}`,
    method: 'put'
  })
}

//删除客户信息
/**
 * 删除客户信息
 * @param {String} custPid - 客户检测信息ID
 * @returns {Promise} 请求Promise对象
 */
export function deleteCustInfo (compCode) {
  return service({
    url: `/api/custBase`,
    method: 'delete',
    data: {
      compCode: compCode,
      enabledState: '2'
    }
  })
}

//新增客户检测信息
/**
 * 新增客户检测信息
 * @param {Object} data - 客户检测信息数据
 * @returns {Promise} 请求Promise对象
 */
export function saveCustInfo (data) {
  return service({
    url: '/api/custInfo',
    method: 'post',
    data: data
  })
}

//修改客户检测信息
/**
 * 修改客户检测信息
 * @param {Object} data - 客户检测信息数据
 * @returns {Promise} 请求Promise对象
 */
export function updateCustInfo (data) {
  return service({
    url: '/api/custInfo',
    method: 'put',
    data: data
  })
}

//查询客户详情
/**
 * 查询客户详情
 * @param {String} custPid - 客户检测信息ID
 * @returns {Promise} 请求Promise对象
 */
export function queryCustInfo (custPid) {
  return service({
    url: `/api/custInfo/${custPid}`,
    method: 'get'
  })
}

//获取签订名称
/**
 * 获取签订名称
 * @param {Object} data - 客户检测信息数据
 * @returns {Promise} 请求Promise对象
 */
export function getCustInfoListBySignName (data) {
  return service({
    url: '/api/custInfo/list/signName',
    method: 'post',
    data: data
  })
}

//监管下载
/**
 * 监管下载
 * @param {Array} data - 选中的数据数组
 * @returns {Promise} 请求Promise对象
 */
export function supervisionDownload (data) {
  return service({
    url: '/api/regeqpquery/download',
    method: 'post',
    data: data
  })
}

//监管确认列表
/**
 * 监管确认列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function supervisionConfirmList (data) {
  return service({
    url: '/api/regeqpquery/confirmList',
    method: 'post',
    data: data
  })
}

//原始记录删除接口 接口名称为deleteOriginalRecord post请求 请求参数为entrustNum，OIDNO
//监管确认列表
/**
 * 监管确认列表
 * @param {Object} data - 参数
 * @returns {Promise} 请求Promise对象
 */
export function deleteOriginalRecord (data) {
  return service({
    url: '/api/testdata/test/deleteTestData',
    method: 'post',
    data: data
  })
}

//---------------------委托任务管理----------------------------

//查询所有委托任务
/**
 * 查询所有委托任务
 * @param {Object} params - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryAllEntrustTask (data) {
  return service({
    url: '/api/entrustTask/queryAllEntrustTask',
    method: 'post',
    data: data
  })
}

/**
 * 导出委托任务（派单列表），参数与查询列表一致
 * @param {Object} data - 查询参数
 * @returns {Promise<Blob>} 文件流
 */
export function exportEntrustTask (data) {
  return service({
    url: '/api/entrustTask/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//保存委托任务(预派单)
/**
 * 保存委托任务(预派单)
 * @param {Object} data - 委托任务数据
 * @returns {Promise} 请求Promise对象
 */
export function saveEntrustTask (data) {
  return service({
    url: '/api/entrustTask/saveEntrustTask',
    method: 'post',
    data: data
  })
}

//正式派单
/**
 * 正式派单
 * @param {Object} data - 派单数据
 * @returns {Promise} 请求Promise对象
 */
export function dispatchTask (data) {
  return service({
    url: '/api/entrustTask/dispatchTask',
    method: 'post',
    data: data
  })
}

//查询职员列表
/**
 * 查询职员列表
 * @param {Object} params - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryEmployee (params) {
  return service({
    url: '/api/employee/queryEmployee',
    method: 'get',
    params: params
  })
}

//---------------------派单管理----------------------------

//撤销派单
/**
 * 撤销派单
 * @param {Object} data - 撤销派单数据，包含entrustNum
 * @returns {Promise} 请求Promise对象
 */
export function backEntrustTask (data) {
  return service({
    url: '/api/entrustTask/backEntrustTask',
    method: 'put',
    data: data
  })
}

/**
 * 查询原始记录列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestDataList (data) {
  return service({
    url: '/api/testdata/list',
    method: 'post',
    data: data
  })
}

//查询需要录入的原始记录详情
/**
 * 查询需要录入的原始记录详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestDataInfo (entrutsNum, firstDate) {
  return service({
    url: '/api/testdata/info/' + entrutsNum + `?firstDate=${firstDate}`,
    method: 'get'
  })
}

//查询暂存过的原始记录详情
/**
 * 查询需要录入的原始记录详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestDataTest (entrutsNum, firstDate) {
  return service({
    url: '/api/testdata/test/' + entrutsNum + `?firstDate=${firstDate}`,
    method: 'get'
  })
}

//保存原始记录/报告信息
/**
 * 保存原始记录/报告信息
 * @param {Object} data - 保存参数
 * @returns {Promise} 请求Promise对象
 */
export function saveTestData (data) {
  return service({
    url: '/api/testdata/test/submit',
    method: 'post',
    data: data
  })
}

/**
 * 保存/更新原始记录备注
 * @param {Object} data - 参数 { operType, entrustNums, ispinfoSubmitRemark, operationDate }
 * @returns {Promise} 请求Promise对象
 */
export function submitTestDataRemark (data) {
  return service({
    url: '/api/testdata/test/submitRemark',
    method: 'post',
    data: data
  })
}

//预览原始记录/报告信息PDF
/**
 * 预览原始记录/报告信息PDF
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function previewTestDataPdf (data) {
  return service({
    url: '/api/testdata/test/pdf',
    method: 'post',
    params: data,
    paramsSerializer: params => {
      // 处理数组参数，将 entrustNums 数组转换为多个同名参数
      const parts = []
      Object.keys(params).forEach(key => {
        const val = params[key]
        if (Array.isArray(val)) {
          val.forEach(v => parts.push(`${key}=${encodeURIComponent(v)}`))
        } else if (val !== null && val !== undefined) {
          parts.push(`${key}=${encodeURIComponent(val)}`)
        }
      })
      return parts.join('&')
    }
  })
}

//报告PDF批量下载
/**
 * 预览原始记录/报告信息PDF
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function previewTestDataPdfdown (data) {
  return service({
    url: '/api/testdata/test/pdf/down',
    method: 'post',
    params: data,
    responseType: 'blob',
    paramsSerializer: params => {
      // 处理数组参数，将 entrustNums 数组转换为多个同名参数
      const parts = []
      Object.keys(params).forEach(key => {
        const val = params[key]
        if (Array.isArray(val)) {
          val.forEach(v => parts.push(`${key}=${encodeURIComponent(v)}`))
        } else if (val !== null && val !== undefined) {
          parts.push(`${key}=${encodeURIComponent(val)}`)
        }
      })
      return parts.join('&')
    }
  })
}

//查询原始记录/报告信息
/**
 * 查询原始记录/报告信息
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestData (data) {
  return service({
    url: '/api/testdata/test/' + data,
    method: 'get'
  })
}

//人员下拉选项
/**
 * 人员下拉选项
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryEmployeeDropdown (data) {
  return service({
    url: '/api/employeeSignPermission/queryEmployeesByPermission',
    method: 'get',
    params: data
  })
}

//原始记录校核列表
/**
 * 原始记录校核列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestDataCheckList (data) {
  return service({
    url: '/api/testdata/testCheck',
    method: 'post',
    data: data
  })
}

//待提交报告列表
/**
 * 待提交报告列表查询
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryPendingSubmitReportList (data) {
  return service({
    url: '/api/testdata/test',
    method: 'post',
    data: data
  })
}

/**
 * 批量提交待提交报告
 * @param {Array} data - 参数 [{ entrustNum, optType: 'Test' }]
 * @returns {Promise} 请求Promise对象
 */
export function submitPendingReportBatch (data) {
  return service({
    url: '/api/testdata/test/submitTestData',
    method: 'post',
    data: data
  })
}

/**
 * 批量退回待提交报告
 * @param {Object} data - 参数 { entrustNums: [] }
 * @returns {Promise} 请求Promise对象
 */
export function batchBackPendingReport (data) {
  return service({
    url: '/api/testdata/test/testBack',
    method: 'post',
    data: data
  })
}

//流程（所有）操作
/**
 * 流程（所有）操作
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function testDataOperator (data) {
  return service({
    url: '/api/testdata/test/operator',
    method: 'post',
    data: data
  })
}

//报告审核、签发列表

/**
 * 报告审核、签发列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestDataAuditApproval (data) {
  return service({
    url: '/api/testdata/auditApproval',
    method: 'post',
    data: data
  })
}

//客户管理功能
/**
 * 客户管理功能列表分页查询
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryCustBaseList (data) {
  return service({
    url: '/api/custBase/list',
    method: 'post',
    data: data
  })
}

/**
 * 客户选择弹窗列表查询
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryCustBaseSelectList (data) {
  return service({
    url: '/api/custBase/selectList',
    method: 'post',
    data: data
  })
}

//客户管理功能保存
/**
 * 客户管理功能保存
 * @param {Object} data - 保存参数
 * @returns {Promise} 请求Promise对象
 */
export function saveCustBase (data) {
  return service({
    url: '/api/custBase',
    method: 'post',
    data: data
  })
}

//客户管理功能查询详情
/**
 * 客户管理功能查询详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryCustBaseDetail (compCode) {
  return service({
    url: '/api/custBase/' + compCode,
    method: 'get'
  })
}

//客户管理功能修改
/**
 * 客户管理功能修改
 * @param {Object} data - 修改参数
 * @returns {Promise} 请求Promise对象
 */
export function updateCustBase (data) {
  return service({
    url: '/api/custBase',
    method: 'put',
    data: data
  })
}

//客户管理功能导入
/**
 * 客户管理功能导入
 * @param {Object} data - 导入参数
 * @returns {Promise} 请求Promise对象
 */
export function importCustBase (data) {
  return service({
    url: '/api/custBase/import',
    method: 'post',
    data: data
  })
}

//客户管理功能导出
/**
 * 客户管理功能导出
 * @param {Object} data - 导出参数
 * @returns {Promise} 请求Promise对象
 */
export function exportCustBase (data) {
  return service({
    url: '/api/custBase/export',
    method: 'post',
    data: data,
    responseType: 'blob' // 设置响应类型为 blob，用于文件下载
  })
}

//查询整改记录列表
/**
 * 查询整改记录列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryRectificationDetailList (data) {
  return service({
    url: '/api/memo/queryRectificationDetailList',
    method: 'post',
    data: data
  })
}

/**
 * 导出整改记录列表（与 queryRectificationDetailList 查询参数一致）
 * @param {Object} data - 查询参数
 * @returns {Promise} Blob 文件流
 */
export function exportRectificationDetailList (data) {
  return service({
    url: '/api/memo/exportRectificationDetailList',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

/**
 * 查询整改记录备忘录子表详情
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 * @param {Object} entrutsNum - 委托任务编号
 */
export function queryByEntrustNum (data) {
  return service({
    url: '/api/memo/queryByEntrustNum',
    method: 'post',
    data: data
  })
}

/**
 * 删除设备信息并退回到原始记录
 * @param {Object} data - 参数 { entrustNum }
 * @returns {Promise} 请求Promise对象
 */
export function deleteMemoAndBackToTest (data) {
  return service({
    url: '/api/memo/revokeEquipmentInfo',
    method: 'post',
    data: data
  })
}

/**
 * 批量更新整改结果和确认方式
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function batchUpdateRectificationInfo (data) {
  return service({
    url: '/api/memo/batchUpdateRectificationInfo',
    method: 'post',
    data: data
  })
}

/**
 * 批量退回到录入
 * @param {Object} data - 参数 { entrustNums: [] }
 * @returns {Promise} 请求Promise对象
 */
export function batchBackToDataEntry (data) {
  return service({
    url: '/api/memo/batchBackToDataEntry',
    method: 'post',
    data: data
  })
}

/**
 * 更新整改确认图片信息
 * @param {Object} data - 更新参数 { entrustTestMemoInfoPid, rectifyConfirmImgUUID }
 * @returns {Promise} 请求Promise对象
 */
export function updateRectifyConfirmImgInfo (data) {
  return service({
    url: '/api/memo/updateRectifyConfirmImgInfo',
    method: 'post',
    data: data
  })
}

//报告待审核列表
/**
 * 报告待审核列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestDataAuditList (data) {
  return service({
    url: '/api/testdata/audit',
    method: 'post',
    data: data
  })
}

//报告待签发列表
/**
 * 报告待签发列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryTestDataApprovalList (data) {
  return service({
    url: '/api/testdata/approval',
    method: 'post',
    data: data
  })
}

//报告校核
/**
 * 报告校核
 * @param {Object} data - 操作参数
 * @returns {Promise} 请求Promise对象
 */
export function testDataCheck (data) {
  return service({
    url: '/api/testdata/test/operator',
    method: 'post',
    data: data
  })
}

//报告审核
/**
 * 报告审核
 * @param {Object} data - 操作参数
 * @returns {Promise} 请求Promise对象
 */
export function testDataAudit (data) {
  return service({
    url: '/api/testdata/test/operator',
    method: 'post',
    data: data
  })
}

//报告签发
/**
 * 报告签发
 * @param {Object} data - 操作参数
 * @returns {Promise} 请求Promise对象
 */
export function testDataApproval (data) {
  return service({
    url: '/api/testdata/test/operator',
    method: 'post',
    data: data
  })
}

//审核校核报告退回
/**
 * 审核校核报告退回
 * @param {Object} data - 操作参数
 * @returns {Promise} 请求Promise对象
 */
export function testDataOperatorBack (data) {
  return service({
    url: '/api/testdata/test/operator',
    method: 'post',
    data: data
  })
}

//签发报告退回
/**
 * 签发报告退回
 * @param {Object} data - 操作参数
 * @returns {Promise} 请求Promise对象
 */
export function testDataApprovalBack (data) {
  return service({
    url: '/api/testdata/test/operator/approvalBack',
    method: 'post',
    data: data
  })
}

//---------------------数据上传----------------------------
//数据上传分页查询
/**
 * 数据上传分页查询
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryUploadDataList (data) {
  return service({
    url: '/api/upload/data/list',
    method: 'post',
    data: data
  })
}

/**
 * 批量退回数据上传
 * @param {Object} data - 参数 { entrustNums: [], backReason: '' }
 * @returns {Promise} 请求Promise对象
 */
export function batchDataUploadBack (data) {
  return service({
    url: '/api/testdata/upload/batchDataUploadBack',
    method: 'post',
    data: data
  })
}

//---------------------视频上传----------------------------
//视频上传管理分页列表
/**
 * 视频上传管理分页列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryUploadFileList (data) {
  return service({
    url: '/api/upload/file/list',
    method: 'post',
    data: data
  })
}

//---------------------报告打印及邮寄----------------------------

//报告列表
/**
 * 报告列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryReportPrintMail (data) {
  return service({
    url: '/api/reportPrintMail/queryReportPrintMail',
    method: 'post',
    data: data
  })
}

//邮寄信息登记
/**
 * 邮寄信息登记
 * @param {Object} data - 邮寄信息数据
 * @returns {Promise} 请求Promise对象
 */
export function registerMail (data) {
  return service({
    url: '/api/reportPrintMail/registerMail',
    method: 'post',
    data: data
  })
}

//更新打印信息
/**
 * 更新打印信息
 * @param {Array} entrustNumList - 委托单编号数组
 * @returns {Promise} 请求Promise对象
 */
export function updatePrintInfo (entrustNumList) {
  return service({
    url: '/api/reportPrintMail/updatePrintInfo',
    method: 'post',
    data: {
      entrustNumList: entrustNumList
    }
  })
}

//--------------------工具箱-----------------------
//查询工具箱及关联设备
/**
 * 查询工具箱及关联设备信息
 * @param {Object} params - 查询参数
 * @param {string} params.usageScenario - 使用场景（如：直梯）
 * @returns {Promise} 请求Promise对象
 */
export function queryToolboxEqptInfos (params) {
  return service({
    url: '/api/toolbox/eqptInfos',
    method: 'get',
    params
  })
}

//根据工具箱号查询关联的设备信息
/**
 * 根据工具箱号查询关联的设备信息
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function queryToolboxList (params) {
  return service({
    url: `/api/toolbox/list/${params}`,
    method: 'get'
  })
}

//批量绑定设备到工具箱
/**
 * 批量绑定设备到工具箱
 * @param {Object} data - 绑定数据
 * @returns {Promise} 请求Promise对象
 */
export function bindToolboxEquipment (data) {
  return service({
    url: '/api/toolbox/bind',
    method: 'post',
    data: data
  })
}

//--------------------发票申请-----------------------
//查询发票申请列表
/**
 * 查询发票申请列表
 * @param {Object} data - 查询参数 { currPage, pageSize, useUntName, applyStatus }
 * @returns {Promise} 请求Promise对象
 */
export function queryInvoiceList (data) {
  return service({
    url: '/api/invoice/list',
    method: 'post',
    data: data
  })
}

//保存发票申请
/**
 * 保存发票申请（新增）
 * @param {Object} data - 发票申请数据
 * @returns {Promise} 请求Promise对象
 */
export function saveInvoice (data) {
  return service({
    url: '/api/invoice/save',
    method: 'post',
    data: data
  })
}

//更新发票申请
/**
 * 更新发票申请（修改）
 * @param {Object} data - 发票申请数据（包含id）
 * @returns {Promise} 请求Promise对象
 */
export function updateInvoice (data) {
  return service({
    url: '/api/invoice/update',
    method: 'post',
    data: data
  })
}

//删除发票申请
/**
 * 删除发票申请
 * @param {String} id - 发票申请ID
 * @returns {Promise} 请求Promise对象
 */
export function deleteInvoice (id) {
  return service({
    url: `/api/invoice/delete/${id}`,
    method: 'delete'
  })
}

//导出发票申请列表
/**
 * 导出发票申请列表
 * @param {Object} data - 查询参数 { currPage, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function exportInvoice (data) {
  return service({
    url: '/api/invoice/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//发票管理（更新发票号和开票时间）
/**
 * 发票管理（更新发票号和开票时间）
 * @param {Object} data - 发票管理数据 { id, invoiceNo, invoiceTime }
 * @returns {Promise} 请求Promise对象
 */
export function manageInvoice (data) {
  return service({
    url: '/api/invoice/manage',
    method: 'post',
    data: data
  })
}

/**
 * 发票红冲保存
 * @param {Object} data - 红冲发票数据
 * @returns {Promise} 请求Promise对象
 */
export function redReversalInvoice (data) {
  return service({
    url: '/api/invoice/redSave',
    method: 'post',
    data: data
  })
}

//查询发票申请详情
/**
 * 查询发票申请详情
 * @param {String} id - 发票申请ID
 * @returns {Promise} 请求Promise对象
 */
export function queryInvoiceDetail (id) {
  return service({
    url: `/api/invoice/detail/${id}`,
    method: 'get'
  })
}

/**
 * 发票操作前校验（是否允许删除/发票管理/红冲）
 * @param {Object} data - 参数 { operType: '1'|'2'|'3', originInvoiceNo: String }
 * @returns {Promise} 请求Promise对象
 */
export function checkInvoiceOperation (data) {
  return service({
    url: '/api/invoice/checkOperation',
    method: 'post',
    data: data
  })
}

//--------------------收款管理-----------------------
/**
 * 查询收款列表
 * @param {Object} data - 查询参数 { pageNum, pageSize, compName, usingUnit, testEmployeeDate, status }
 * @returns {Promise} 请求Promise对象
 */
export function queryReceivablesList (data) {
  return service({
    url: '/api/receipt/list',
    method: 'post',
    data: data
  })
}

/**
 * 保存收款信息（批量保存）
 * @param {Array} data - 收款数据数组
 * @returns {Promise} 请求Promise对象
 */
export function saveReceivables (data) {
  return service({
    url: '/api/receipt/save',
    method: 'post',
    data: data
  })
}

/**
 * 撤销收款
 * @param {String} receiptNo - 收款编号
 * @returns {Promise} 请求Promise对象
 */
export function cancelReceivables (receiptNo) {
  return service({
    url: `/api/receipt/cancel/${receiptNo}`,
    method: 'post'
  })
}

/**
 * 导出收款列表
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function exportReceivables (data) {
  return service({
    url: '/api/receipt/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//--------------------产值统计-----------------------
/**
 * 产值统计查询
 * @param {Object} data - 查询参数 { compName, testEmployeeDate: [start, end] }
 * @returns {Promise} 请求Promise对象
 */
export function queryProductionSheet (data) {
  return service({
    url: '/api/financial/productionSheet',
    method: 'post',
    data: data
  })
}

/**
 * 产值统计导出
 * @param {Object} data - 导出参数（与查询参数一致）
 * @returns {Promise} 请求Promise对象（Blob）
 */
export function exportProductionSheet (data) {
  return service({
    url: '/api/financial/productionSheet/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

/**
 * 保存层站设置
 * @param {Object} data - 层站设置数据
 * @returns {Promise} 请求Promise对象
 */
export function saveElevatorSetting (data) {
  return service({
    url: '/api/elevatorSetting',
    method: 'post',
    data: data
  })
}

/**
 * 更新层站设置
 * @param {Object} data - 层站设置数据（包含id）
 * @returns {Promise} 请求Promise对象
 */
export function updateElevatorSetting (data) {
  return service({
    url: '/api/elevatorSetting',
    method: 'put',
    data: data
  })
}

/**
 * 查询层站设置
 * @param {String} entrustNum - 预委托编码
 * @returns {Promise} 请求Promise对象
 */
export function getElevatorSetting (entrustNum) {
  return service({
    url: `/api/elevatorSetting/${entrustNum}`,
    method: 'get'
  })
}

//--------------------收款统计-----------------------
/**
 * 收款统计查询
 * @param {Object} data - 查询参数 { compName, receiptTime: [start, end] }
 * @returns {Promise} 请求Promise对象
 */
export function queryReceiptSheet (data) {
  return service({
    url: '/api/financial/receiptSheet',
    method: 'post',
    data: data,
    timeout: 120000 // 统计查询较慢，单独放宽超时
  })
}

/**
 * 收款统计导出
 * @param {Object} data - 导出参数（与查询参数一致）
 * @returns {Promise} 请求Promise对象（Blob）
 */
export function exportReceiptSheet (data) {
  return service({
    url: '/api/financial/receiptSheet/export',
    method: 'post',
    data: data,
    responseType: 'blob',
    timeout: 120000
  })
}

//--------------------客户应收款统计-----------------------
/**
 * 客户应收款统计查询
 * @param {Object} data - 查询参数 { compName, testEmployeeDate: [start, end], currPage, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryReceivableSheet (data) {
  return service({
    url: '/api/financial/receivableSheet',
    method: 'post',
    data: data
  })
}

/**
 * 客户应收款统计导出
 * @param {Object} data - 导出参数（与查询参数一致）
 * @returns {Promise} 请求Promise对象（Blob）
 */
export function exportReceivableSheet (data) {
  return service({
    url: '/api/financial/receivableSheet/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//--------------------绩效统计-----------------------
/**
 * 绩效统计查询
 * @param {Object} data - 查询参数 { yearMonth, confirmationStatus, currPage, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryPerformanceStatistics (data) {
  return service({
    url: '/api/performance/statistics/query',
    method: 'post',
    data: data
  })
}

/**
 * 绩效统计导出
 * @param {Object} data - 导出参数（与查询参数一致）
 * @returns {Promise} 请求Promise对象（Blob）
 */
export function exportPerformanceStatistics (data) {
  return service({
    url: '/api/performance/statistics/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

/**
 * 查询保底绩效配置列表
 * @param {Object} data - 查询参数 { currPage, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryPerformanceBaseList (data) {
  return service({
    url: '/api/performance/baseConfig/query',
    method: 'post',
    data: data
  })
}

/**
 * 保存保底绩效配置列表
 * @param {Array} data - 保底绩效配置数组
 * @returns {Promise} 请求Promise对象
 */
export function confirmPerformanceBaseList (data) {
  return service({
    url: '/api/performance/baseConfig/save',
    method: 'post',
    data: data
  })
}

//--------------------返点计算管理-----------------------
/**
 * 查询返点计算列表
 * @param {Object} data - 查询参数 { pageNum, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryRebateList (data) {
  return service({
    url: '/api/receipt/rebatelist',
    method: 'post',
    data: data
  })
}

/**
 * 批量确认返点费用
 * @param {Object} data - 确认数据 { entrustNums, confirmUserId, confirmUserName }
 * @returns {Promise} 请求Promise对象
 */
export function confirmRebate (data) {
  return service({
    url: '/api/receipt/confirm',
    method: 'post',
    data: data
  })
}

/**
 * 修改返点费用
 * @param {Object} data - 修改数据 { entrustNum, rebateFee, brokerTaxRate, brokerTaxAmount, updateTime }
 * @returns {Promise} 请求Promise对象
 */
export function updateRebate (data) {
  return service({
    url: '/api/receipt/updateRebate',
    method: 'post',
    data: data
  })
}

/**
 * 检查返点费是否已确认
 * @param {String} entrustNum - 委托编号
 * @returns {Promise} 请求Promise对象
 */
export function checkRebateConfirmed (entrustNum) {
  return service({
    url: '/api/receipt/checkRebateConfirmed/' + entrustNum,
    method: 'get'
  })
}

/**
 * 导出返点计算列表
 * @param {Object} data - 查询参数（同查询参数）
 * @returns {Promise} 请求Promise对象
 */
export function exportRebate (data) {
  return service({
    url: '/api/receipt/exportRebate',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//--------------------检测配合费用管理-----------------------
/**
 * 查询配合费信息列表
 * @param {Object} data - 查询参数 { pageNum, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryCoordinationFeeList (data) {
  return service({
    url: '/api/cooperationfee/list',
    method: 'post',
    data: data
  })
}

/**
 * 批量保存配合费
 * @param {Array} data - 保存数据数组 [{ oidNo, entrustNum, cooperationFee }]
 * @returns {Promise} 请求Promise对象
 */
export function saveCoordinationFee (data) {
  return service({
    url: '/api/cooperationfee/save',
    method: 'post',
    data: data
  })
}

/**
 * 修改配合费
 * @param {Object} data - 修改数据 { feeId, oidNo, cooperationFee }
 * @returns {Promise} 请求Promise对象
 */
export function updateCoordinationFee (data) {
  return service({
    url: '/api/cooperationfee/update',
    method: 'post',
    data: data
  })
}

/**
 * 导出配合费列表
 * @param {Object} data - 查询参数（使用查询的参数）
 * @returns {Promise} 请求Promise对象
 */
export function exportCoordinationFee (data) {
  return service({
    url: '/api/cooperationfee/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//--------------------检测其他费用管理-----------------------
/**
 * 查询其他检测费用列表
 * @param {Object} data - 查询参数 { currPage, pageSize, compName, compNames, useUntName, oidNo, testDateRange, enabledState }
 * @returns {Promise} 请求Promise对象
 */
export function queryOtherFeeList (data) {
  return service({
    url: '/api/otherFee/list',
    method: 'post',
    data: data
  })
}

/**
 * 批量保存其他检测费用
 * @param {Array} data - [{ oidNo, entrustNum, feeDiscount, testOtherFee }]
 * @returns {Promise} 请求Promise对象
 */
export function saveOtherFee (data) {
  return service({
    url: '/api/otherFee',
    method: 'post',
    data: data
  })
}

/**
 * 修改其他检测费用
 * @param {Object} data - { id, entrustNum, oidNo, testOtherFee, feeDiscount }
 * @returns {Promise} 请求Promise对象
 */
export function updateOtherFee (data) {
  return service({
    url: '/api/otherFee',
    method: 'put',
    data: data
  })
}

/**
 * 批量删除其他检测费用
 * @param {Array<string>} ids - 记录 id 数组
 * @returns {Promise} 请求Promise对象
 */
export function deleteOtherFee (ids) {
  return service({
    url: '/api/otherFee',
    method: 'delete',
    data: ids
  })
}

/**
 * 导出其他检测费用列表
 * @param {Object} data - 查询参数（与列表查询一致）
 * @returns {Promise} 请求Promise对象（Blob）
 */
export function exportOtherFee (data) {
  return service({
    url: '/api/otherFee/export',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//--------------------综合台账管理-----------------------
/**
 * 查询综合台账列表
 * @param {Object} data - 查询参数 { pageNum, pageSize, compName, usingUnit, testEmployeeDate }
 * @returns {Promise} 请求Promise对象
 */
export function queryLedgerList (data) {
  return service({
    url: '/api/comprehensive/list',
    method: 'post',
    data: data,
    timeout: 0
  })
}

/** 综合台账合计行（与 list 拆分，异步加载） */
export function queryLedgerSummary (data) {
  return service({
    url: '/api/comprehensive/summary',
    method: 'post',
    data: data,
    timeout: 0
  })
}

/**
 * 导出综合台账列表
 * @param {Object} data - 查询参数（与查询参数一致）
 * @returns {Promise} 请求Promise对象
 */
export function exportLedger (data) {
  return service({
    url: '/api/comprehensive/export',
    method: 'post',
    data: data,
    responseType: 'blob',
    timeout: 0
  })
}

/**
 * 客户对账单查询列表
 * @param {Object} data - 查询参数 { currPage, pageSize, compNames, usingUnit, houseName, businessContact, eqpAreaName, inspectionMonthList, checkedCount, testFee, isReceipt, invoiceTimeRange, isMailing, progress }
 * @returns {Promise} 请求Promise对象
 */
export function queryReconciliationList (data) {
  return service({
    url: '/api/comprehensive/reconciliation/list',
    method: 'post',
    data: data
  })
}

/**
 * 导出客户对账单
 * @param {Object} data - 查询参数（与查询参数一致）
 * @returns {Promise} 请求Promise对象（Blob）
 */
export function exportReconciliation (data) {
  return service({
    url: '/api/comprehensive/reconciliation/export',
    method: 'post',
    data: data,
    responseType: 'blob',
    timeout: 0
  })
}

/**
 * 综合报告查询列表
 * @param {Object} data - 查询参数 { oidno, usingUnit, inspectionMonthList, testDateRange, currPage, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryComprehensiveReportList (data) {
  return service({
    url: '/api/comprehensive/reportList',
    method: 'post',
    data: data
  })
}

/**
 * 重新生成备忘录 Word/PDF
 * @param {Object} data - { entrustNum } 或 { entrustTestMemoPid }
 * @returns {Promise} 请求Promise对象
 */
export function regenerateMemoPdf (data) {
  return service({
    url: '/api/memo/regenerateMemoPdf',
    method: 'post',
    data: data
  })
}

/**
 * 批量下载备忘录PDF
 * @param {Object} data - 请求参数 { entrustNums: [] }
 * @returns {Promise} 请求Promise对象（含响应头，用于解析文件名）
 */
export function downloadComprehensivePdf (data) {
  const token = getToken()
  return axios({
    baseURL: process.env.VUE_APP_BASE_API || '',
    url: '/api/comprehensive/downloadPdf',
    method: 'post',
    data: data,
    responseType: 'blob',
    headers: token ? { Authorization: token } : undefined
  })
}

/**
 * 批量删除综合报告本地PDF文件
 * @param {Object} data - 请求参数 { entrustNums: [] }
 * @returns {Promise} 请求Promise对象
 */
export function deleteLocalPdf (data) {
  return service({
    url: '/api/comprehensive/deleteLocalPdf',
    method: 'post',
    data: data
  })
}

//--------------------数据上传SSE-----------------------
/**
 * 数据上传（触发SSE任务）
 * @param {Array} entrustNums - 委托单编号数组
 * @param {String} type - 类型参数（必需）
 * @returns {Promise} 请求Promise对象
 */
export function uploadData (entrustNums, type = '') {
  return service({
    url: '/api/upload/data',
    method: 'post',
    params: {
      entrustNums: entrustNums,
      type: type || ''
    },
    paramsSerializer: params => {
      // 处理数组参数，将 entrustNums 数组转换为多个同名参数
      const parts = []
      Object.keys(params).forEach(key => {
        const val = params[key]
        if (Array.isArray(val)) {
          val.forEach(v => parts.push(`${key}=${encodeURIComponent(v)}`))
        } else if (val !== null && val !== undefined && val !== '') {
          parts.push(`${key}=${encodeURIComponent(val)}`)
        }
      })
      return parts.join('&')
    }
  })
}

//--------------------设备详情-----------------------
/**
 * 获取设备详情
 * @param {String} entrustNum - 委托编号
 * @param {Number} isDeviceConfirmation - 固定值为1
 * @returns {Promise} 请求Promise对象
 */
export function getDeviceDetail (entrustNum, isDeviceConfirmation) {
  return service({
    url: '/api/memo/getDeviceDetail',
    method: 'post',
    data: {
      entrustNum: entrustNum,
      isDeviceConfirmation: isDeviceConfirmation
    }
  })
}

/**
 * 确认设备信息
 * @param {Object} data - 设备确认参数
 * @returns {Promise} 请求Promise对象
 */
export function confirmEquipmentInfo (data) {
  return service({
    url: '/api/memo/confirmEquipmentInfo',
    method: 'post',
    data: data
  })
}

/**
 * 设备确认参照：分页查询历史设备确认列表
 * @param {Object} data - { currPage, pageSize, useUntName, houseName, eqpName }
 */
export function queryDeviceConfirmationList (data) {
  return service({
    url: '/api/deviceConfirmation/queryList',
    method: 'post',
    data
  })
}

/**
 * 设备确认参照：按已选字段配置拉取单条设备信息（回填）
 * @param {string|number} id - 列表记录 id
 */
export function queryDeviceConfirmationByFields (id) {
  return service({
    url: `/api/deviceConfirmation/queryByFields/${id}`,
    method: 'get'
  })
}

/**
 * 设备确认参照：查询当前用户的字段参照配置
 * @param {Object} params - 一般为 { eid }，eid 与后端约定（常用为 entrustTestMemoPid）
 */
export function queryDeviceConfigByEid (params) {
  return service({
    url: '/api/deviceConfirmation/config/queryDeviceConfigByEid',
    method: 'get',
    params
  })
}

/**
 * 设备确认参照：保存字段参照配置
 * @param {Object} data - { deviceFields: string[] }，可选携带 id、employeePid 等由查询接口返回的字段
 */
export function saveOrUpdateDeviceReferenceConfig (data) {
  return service({
    url: '/api/deviceConfirmation/config/saveOrUpdate',
    method: 'post',
    data
  })
}

//--------------------付款管理-----------------------
/**
 * 查询付款管理列表
 * @param {Object} data - 查询参数 { currPage, pageSize, category, customerName, useUnit, testDateStart, testDateEnd, paymentStatus }
 * @returns {Promise} 请求Promise对象
 */
export function queryPaymentList (data) {
  return service({
    url: '/api/paymanagement/list',
    method: 'post',
    data: data
  })
}

/**
 * 保存付款信息
 * @param {Object} data - 付款数据
 * @returns {Promise} 请求Promise对象
 */
export function savePayment (data) {
  return service({
    url: '/api/paymanagement/pay',
    method: 'post',
    data: data
  })
}

/**
 * 撤销付款
 * @param {Object} data - 撤销数据 { dataIds }
 * @returns {Promise} 请求Promise对象
 */
export function cancelPayment (data) {
  return service({
    url: '/api/paymanagement/cancel',
    method: 'post',
    data: data
  })
}

/**
 * 查询所有未开票且已生成备忘录的电梯识别码
 * @param {Object} data - 请求体（如 usingUnit、houseName、inspectionMonthList、oidNo 等）
 * @returns {Promise} 请求Promise对象
 */
export function invoiceOidnos (data) {
  return service({
    url: '/api/invoice/oidnos',
    method: 'post',
    data
  })
}

/**
 * 查询已保存的检测数据列表（用于参照）
 * @param {Object} data - 查询参数 { currPage, pageSize, USE_UNT_NAME, projectName, EQP_NAME, testEmployeeDate }
 * @returns {Promise} 请求Promise对象
 */
export function querySavedTestDataList (data) {
  return service({
    url: '/api/testdata/test/querySavedList',
    method: 'post',
    data: data
  })
}

/**
 * 复制报告检测数据信息（用于参照回填）
 * @param {String} entrustNum - 委托单编号
 * @returns {Promise} 请求Promise对象
 */
export function copyReportTestDataInfo (entrustNum) {
  return service({
    url: `/api/testdata/test/copyReportTestDataInfo/${entrustNum}`,
    method: 'get'
  })
}

/**
 * 根据内容编码查询详情列表
 * @param {Object} data - 查询参数 { configTestItemsContentCode, checkResult }
 * @returns {Promise} 请求Promise对象
 */
export function queryDetailListByContentCode (data) {
  return service({
    url: '/api/configTestContentDetail/queryDetailListByContentCode',
    method: 'post',
    data: data
  })
}

//--------------------符合性声明上传管理-----------------------
/**
 * 查询符合性声明上传记录列表
 * @param {Object} data - 查询参数 { useUnitName, uploadSuccess, uploadDateStr, currPage, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryConformanceUploadRecordList (data) {
  return service({
    url: '/api/conformanceUploadRecord/queryUploadRecordList',
    method: 'post',
    data: data
  })
}

/**
 * 查询符合性声明上传记录详情
 * @param {Object} data - 查询参数 { uploadRecordPid }
 * @returns {Promise} 请求Promise对象
 */
export function queryConformanceUploadRecordDetail (data) {
  return service({
    url: '/api/conformanceUploadRecord/queryUploadRecordDetail',
    method: 'post',
    data: data
  })
}

/**
 * 保存符合性声明上传记录
 * @param {Object} data - 保存参数 { useUntName, uploadSuccess, uploadFailReason, uploadDate, uploadPerson, uploadPersonPid, remark }
 * @returns {Promise} 请求Promise对象
 */
export function saveConformanceUploadRecord (data) {
  return service({
    url: '/api/conformanceUploadRecord/saveUploadRecord',
    method: 'post',
    data: data
  })
}

/**
 * 更新符合性声明上传记录
 * @param {Object} data - 更新参数 { uploadRecordPid, useUnitName, uploadSuccess, uploadFailReason, uploadDate, uploadPerson, uploadPersonPid, remark }
 * @returns {Promise} 请求Promise对象
 */
export function updateConformanceUploadRecord (data) {
  return service({
    url: '/api/conformanceUploadRecord/updateUploadRecord',
    method: 'post',
    data: data
  })
}

/**
 * 批量删除符合性声明上传记录
 * @param {Object} data - 删除参数 { uploadRecordPids: string[] }
 * @returns {Promise} 请求Promise对象
 */
export function deleteConformanceUploadRecord (data) {
  return service({
    url: '/api/conformanceUploadRecord/deleteUploadRecord',
    method: 'post',
    data: data
  })
}

/**
 * 查询使用单位名称列表
 * @returns {Promise} 请求Promise对象
 */
export function queryUseUnitNameList () {
  return service({
    url: '/api/conformanceUploadRecord/queryUploadRecordList',
    method: 'post',
    data: {}
  })
}

/**
 * 生成声明 - 查询设备列表
 * @param {Object} data - 查询参数 { useUntName, houseName, inspectionMonth }
 * @returns {Promise} 请求Promise对象
 */
export function queryInspectionEquipmentList (data) {
  return service({
    url: '/api/conformance/inspectionEquipmentList',
    method: 'post',
    data: data
  })
}

/**
 * 生成声明 - 导出设备列表
 * @param {Object} params - 查询参数 { useUntName, houseName, inspectionMonth }
 * @returns {Promise} 请求Promise对象
 */
export function exportInspectionEquipmentList (params) {
  return service({
    url: '/api/conformance/exportInspectionEquipmentList',
    method: 'get',
    params: params,
    responseType: 'blob'
  })
}

/**
 * 生成声明 - 按项目统计未完成台数（使用单位+小区+到检年月）
 * @param {Object} data - { useUntName, houseName, inspectionMonthList: [yyyy-MM] }
 */
export function queryProjectIncompleteCount (data) {
  return service({
    url: '/api/conformance/projectIncompleteCount',
    method: 'post',
    data: data
  })
}

/**
 * 生成声明 - 保存
 * @param {Object} data - 保存参数 { equipmentList: [] }
 * @returns {Promise} 请求Promise对象
 */
export function saveConformanceDeclaration (data) {
  return service({
    url: '/api/conformance/save',
    method: 'post',
    data: data
  })
}

/**
 * 声明批量下载
 * @param {Object} data - 请求参数 { confNums: [] }
 * @returns {Promise} 请求Promise对象
 */
export function batchDownloadConformance (data) {
  return service({
    url: '/api/conformance/batchDownload',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

//--------------------绩效管理-----------------------
/**
 * 查询绩效列表
 * @param {Object} data - 查询参数 { employeePid, yearMonth, confirmationStatus, currPage, pageSize }
 * @returns {Promise} 请求Promise对象
 */
export function queryPerformanceList (data) {
  return service({
    url: '/api/performance/query',
    method: 'post',
    data: data
  })
}

/**
 * 查询绩效详情
 * @param {Object} data - 查询参数 { employeePid, yearMonth, confirmationStatus, performanceId }
 * @returns {Promise} 请求Promise对象
 */
export function queryPerformanceDetails (data) {
  return service({
    url: '/api/performance/details',
    method: 'post',
    data: data
  })
}

/**
 * 批量确认绩效
 * @param {Object} data - 确认参数 { employeePids, inspectionYearMonth, confirmedBy }
 * @returns {Promise} 请求Promise对象
 */
export function confirmPerformance (data) {
  return service({
    url: '/api/performance/confirm',
    method: 'post',
    data: data
  })
}

//--------------------委托信息管理-----------------------
/**
 * 委托信息分页查询
 * @param {Object} data - 查询参数 { currPage, pageSize, claimStatus, compName, usingUnit, houseName, OIDNO, inspectionMonth, claimDate }
 * @returns {Promise} 请求Promise对象
 */
export function queryEntrustList (data) {
  return service({
    url: '/api/custInfo/pageEntrList',
    method: 'post',
    data: data
  })
}

/**
 * 导出委托列表（与 pageEntrList 查询参数一致）
 * @param {Object} data - 查询参数
 * @returns {Promise} Blob 文件流
 */
export function exportEntrList (data) {
  return service({
    url: '/api/custInfo/exportEntrList',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

/**
 * 批次详情查询
 * @param {String} batchNum - 批次号
 * @returns {Promise} 请求Promise对象
 */
export function getBatchDetail (batchNum) {
  return service({
    url: `/api/custInfo/calim/${batchNum}`,
    method: 'post'
  })
}

/**
 * 批量认领委托信息
 * @param {Object} data - 认领参数 { custPids, claimDate, claimOperator }
 * @returns {Promise} 请求Promise对象
 */
export function claimCustInfos (data) {
  return service({
    url: '/api/custInfo/calimCustInfos',
    method: 'post',
    data: data
  })
}

/**
 * 更新认领信息
 * @param {Object} data - 更新参数 { custPids, claimDate, claimOperator }
 * @returns {Promise} 请求Promise对象
 */
export function updateClaimInfo (data) {
  return service({
    url: '/api/custInfo/updateCalimCustInfos',
    method: 'post',
    data: data
  })
}

/**
 * 批量认领撤销
 * @param {Object} data - 参数 { entrNums: string[] }
 * @returns {Promise} 请求Promise对象
 */
export function cancelClaimInfos (data) {
  return service({
    url: '/api/custInfo/cancelClaimInfos',
    method: 'post',
    data: data
  })
}

/**
 * 批量添加待确认备注
 * @param {Object} data - 参数 { entrNums: string[], pendingConfirmRemark: string }
 * @returns {Promise} 请求Promise对象
 */
export function batchPendingConfirmRemark (data) {
  return service({
    url: '/api/custInfo/batchPendingConfirmRemark',
    method: 'post',
    data: data
  })
}

/**
 * 删除委托信息
 * @param {Array} custPidList - custPid数组
 * @returns {Promise} 请求Promise对象
 */
export function deleteEntrustInfo (custPidList) {
  return service({
    url: '/api/custInfo/deleteCustomerInfo',
    method: 'post',
    data: custPidList
  })
}

/**
 * 导入委托信息
 * @param {Object} data - 委托信息数据（FormData格式）
 * @returns {Promise} 请求Promise对象
 */
export function importEntrustInfo (data) {
  return service({
    url: '/api/custInfo/importEntrust',
    method: 'post',
    data: data
  })
}

/**
 * 导出委托信息
 * @param {Object} data - 查询参数
 * @returns {Promise} 请求Promise对象
 */
export function exportEntrustInfo (data) {
  return service({
    url: '/api/custInfo/exportEntrust',
    method: 'post',
    data: data,
    responseType: 'blob'
  })
}

/**
 * 更新委托信息状态
 * @param {String} enableState - 启用状态（0-已确认，1-未确认）
 * @param {String} custPid - 委托信息ID
 * @returns {Promise} 请求Promise对象
 */
export function updateEntrustInfoState (enableState, custPid) {
  return service({
    url: `/api/custInfo/entrustState/${custPid}?enableState=${enableState}`,
    method: 'put'
  })
}

// ==================== 检测仪器工具箱管理 ====================

/**
 * 查询工具箱列表
 */
export function getToolboxList () {
  return service({
    url: '/api/equipmentToolbox/list',
    method: 'get'
  })
}

/**
 * 查询工具箱详情
 */
export function getToolboxDetail (toolboxNum) {
  return service({
    url: `/api/equipmentToolbox/detail/${toolboxNum}`,
    method: 'get'
  })
}

/**
 * 新增工具箱
 */
export function addToolbox (data) {
  return service({
    url: '/api/equipmentToolbox/add',
    method: 'post',
    data
  })
}

/**
 * 更新工具箱
 */
export function updateToolbox (data) {
  return service({
    url: '/api/equipmentToolbox/update',
    method: 'put',
    data
  })
}

/**
 * 删除工具箱
 */
export function deleteToolbox (toolboxNum) {
  return service({
    url: `/api/equipmentToolbox/delete/${toolboxNum}`,
    method: 'delete'
  })
}

/**
 * 生成工具箱号
 */
export function generateToolboxNum () {
  return service({
    url: '/api/equipmentToolbox/generateNum',
    method: 'get'
  })
}

/**
 * 查询工具箱中的仪器列表
 */
export function getToolboxInstruments (toolboxNum) {
  return service({
    url: `/api/toolbox/list/${toolboxNum}`,
    method: 'get'
  })
}

/**
 * 查询可添加的仪器列表（排除已被其他责任人使用的仪器）
 */
export function getAvailableInstruments (params) {
  return service({
    url: '/api/toolbox/available',
    method: 'get',
    params: params
  })
}

/**
 * 添加仪器到工具箱
 */
export function addInstrumentsToToolbox (data) {
  return service({
    url: '/api/toolbox/bind',
    method: 'post',
    data
  })
}

/**
 * 从工具箱删除仪器
 */
export function removeInstrumentsFromToolbox (data) {
  return service({
    url: '/api/toolbox/unbind',
    method: 'post',
    data
  })
}

/**
 * 首页待办事项汇总
 */
export function getDataSummary () {
  return service({
    url: '/api/home/getDataSummary',
    method: 'post'
  })
}

/**
 * 首页数据统计汇总
 */
export function getStatistics () {
  return service({
    url: '/api/home/getStatistics',
    method: 'post'
  })
}

export default {
  getContractList,
  getContractDetail,
  createContract,
  updateContract,
  deleteContract,
  saveStandard,
  updateStandard,
  deleteStandard,
  queryStandard,
  queryTemplate,
  saveTemplate,
  queryAllConfigReport,
  deleteTemplate,
  exportTemplate,
  updateTemplate,
  templateQueryAllConfigReport,
  queryReportTypeDetail,
  queryConfigReportDetail,
  queryConfigTestItemDetail,
  queryConfigTestItemsChildDetail,
  queryConfigTestItemsContentDetail,
  saveReportType,
  saveConfigReport,
  saveConfigTestItems,
  saveConfigTestItemsChild,
  saveConfigTestItemsContent,
  updateReportType,
  updateConfigReport,
  updateConfigTestItems,
  updateConfigTestItemsChild,
  updateConfigTestItemsContent,
  deleteReportType,
  deleteConfigReport,
  deleteConfigTestItems,
  deleteConfigTestItemsChild,
  queryEqptList,
  saveEqpt,
  queryEqptDetail,
  updateEqpt,
  exportEqptExcel,
  downloadEqptTemplate,
  importEqptData,
  queryEqptVerificationList,
  saveEqptVerification,
  queryEqptVerification,
  updateEqptVerification,
  exportEqptVerificationExcel,
  deleteEqptVerification,
  queryEqptVerificationByEqptNum,
  queryAllTemplatePidAndName,
  queryAllStandardPidCodeName,
  queryEqptGuidAndName,
  queryPermissionsByEmployee,
  batchSaveOrUpdateEmployeeSignPermission,
  querySysDepartments,
  queryAvailableJgEqpNames,
  queryCustInfoList,
  importCustInfo,
  exportCustInfo,
  updateCustInfoState,
  saveCustInfo,
  updateCustInfo,
  getCustInfoListBySignName,
  supervisionDownload,
  supervisionConfirmList,
  queryAllEntrustTask,
  exportEntrustTask,
  saveEntrustTask,
  queryEmployee,
  backEntrustTask,
  queryTestDataList,
  queryTestDataInfo,
  queryTestDataTest,
  saveTestData,
  submitTestDataRemark,
  previewTestDataPdf,
  queryTestData,
  queryEmployeeDropdown,
  queryTestDataCheckList,
  queryPendingSubmitReportList,
  submitPendingReportBatch,
  batchBackPendingReport,
  testDataOperator,
  queryTestDataAuditApproval,
  queryCustBaseList,
  queryCustBaseSelectList,
  saveCustBase,
  queryCustBaseDetail,
  updateCustBase,
  deleteCustInfo,
  importCustBase,
  exportCustBase,
  queryRectificationDetailList,
  exportRectificationDetailList,
  queryByEntrustNum,
  deleteMemoAndBackToTest,
  batchUpdateRectificationInfo,
  batchBackToDataEntry,
  updateRectifyConfirmImgInfo,
  queryCustInfo,
  queryTestDataAuditList,
  queryTestDataApprovalList,
  testDataCheck,
  testDataAudit,
  testDataApproval,
  testDataOperatorBack,
  testDataApprovalBack,
  queryReportPrintMail,
  registerMail,
  updatePrintInfo,
  queryUploadFileList,
  queryUploadDataList,
  batchDataUploadBack,
  previewTestDataPdfdown,
  queryToolboxList,
  bindToolboxEquipment,
  queryInvoiceList,
  saveInvoice,
  updateInvoice,
  deleteInvoice,
  exportInvoice,
  manageInvoice,
  redReversalInvoice,
  queryInvoiceDetail,
  checkInvoiceOperation,
  queryReceivablesList,
  saveReceivables,
  cancelReceivables,
  exportReceivables,
  queryProductionSheet,
  exportProductionSheet,
  queryReceiptSheet,
  exportReceiptSheet,
  queryRebateList,
  confirmRebate,
  updateRebate,
  exportRebate,
  queryCoordinationFeeList,
  saveCoordinationFee,
  updateCoordinationFee,
  exportCoordinationFee,
  queryOtherFeeList,
  saveOtherFee,
  updateOtherFee,
  deleteOtherFee,
  exportOtherFee,
  queryLedgerList,
  queryLedgerSummary,
  queryReconciliationList,
  exportReconciliation,
  queryComprehensiveReportList,
  regenerateMemoPdf,
  downloadComprehensivePdf,
  exportLedger,
  queryPaymentList,
  savePayment,
  cancelPayment,
  saveElevatorSetting,
  updateElevatorSetting,
  getElevatorSetting,
  uploadData,
  invoiceOidnos,
  getDeviceDetail,
  confirmEquipmentInfo,
  queryDeviceConfirmationList,
  queryDeviceConfirmationByFields,
  queryDeviceConfigByEid,
  saveOrUpdateDeviceReferenceConfig,
  querySavedTestDataList,
  copyReportTestDataInfo,
  queryConformanceUploadRecordList,
  queryConformanceUploadRecordDetail,
  saveConformanceUploadRecord,
  updateConformanceUploadRecord,
  deleteConformanceUploadRecord,
  queryUseUnitNameList,
  queryInspectionEquipmentList,
  exportInspectionEquipmentList,
  queryProjectIncompleteCount,
  saveConformanceDeclaration,
  batchDownloadConformance,
  queryPerformanceList,
  queryPerformanceDetails,
  confirmPerformance,
  queryEntrustList,
  exportEntrList,
  getBatchDetail,
  claimCustInfos,
  updateClaimInfo,
  cancelClaimInfos,
  batchPendingConfirmRemark,
  deleteEntrustInfo,
  queryToolboxEqptInfos,
  getDataSummary,
  getStatistics
}
